import { makeAutoObservable, runInAction } from 'mobx';
import type { Agent, ChatMessage, ChatModelMode } from '../entity';
import { applyBaseStore } from './BaseStore';
import type { BaseStore } from './BaseStore';
import { ChatStreamingService } from '../services/ChatStreamingService';

type BidChatSession = {
  messages: ChatMessage[];
  status: string;
  modelMode: ChatModelMode;
  lastPrompt: string;
  threadId: string;
};

export interface BidCaseStore extends BaseStore {}

export class BidCaseStore {
  private readonly bidChatStreamingService = new ChatStreamingService();

  bidChatSessions: Record<string, BidChatSession> = {};

  bid_agents: string[] = [];

  bid_agent_files = [];

  bidRagUploadActive = false;

  bidRagUploadTotal = 0;

  bidRagUploadCompleted = 0;

  bidRagUploadCurrentFile = '';

  bidRagUploadFailures: string[] = [];

  bidCaseStartActive = false;

  bidCaseStartError = '';

  bid_agent: Agent = undefined;

  file_content: Blob = undefined;

  constructor() {
    applyBaseStore(this);
    makeAutoObservable(this, { bidChatStreamingService: false } as any);
  }

  get activeBidChatSession(): BidChatSession {
    const caseKey = this.activeBidChatKey;
    if (!this.bidChatSessions[caseKey]) {
      this.bidChatSessions[caseKey] = {
        messages: [],
        status: '',
        modelMode: 'fast',
        lastPrompt: '',
        threadId: `bid-${caseKey}`
      };
    }
    return this.bidChatSessions[caseKey];
  }

  get activeBidChatKey(): string {
    return String(this.bid_agent?.agent_azure_id || this.bid_agent?.agent_name || 'no-case');
  }

  get canCloseActiveBidCase(): boolean {
    const activeCase = this.bid_agent;
    return Boolean(activeCase?.agent_name) && (
      activeCase?.bid_status !== 'running' || !activeCase?.agent_azure_id
    );
  }

  setBidChatModelMode(mode: ChatModelMode) {
    if (mode !== 'fast' && mode !== 'thinking') return;
    this.activeBidChatSession.modelMode = mode;
  }

  startNewBidChat() {
    if (this.bidChatStreamingService.isActiveRequest()) return;
    const session = this.activeBidChatSession;
    session.messages = [];
    session.status = '';
    session.lastPrompt = '';
    session.threadId = `bid-${this.activeBidChatKey}-${Date.now()}`;
  }

  cancelBidChat() {
    this.bidChatStreamingService.cancelActiveRequest();
  }

  async sendBidChat(prompt: string) {
    const question = String(prompt || '').trim();
    const agentId = this.bid_agent?.agent_azure_id;
    const session = this.activeBidChatSession;
    if (!question || !agentId || this.bidChatStreamingService.isActiveRequest()) return;

    const userMessage: ChatMessage = { role: 'user', content: question, thread_id: session.threadId };
    runInAction(() => {
      session.lastPrompt = question;
      session.status = 'Anfrage wird verarbeitet.';
      session.messages.push(userMessage, { role: 'assistant', content: '', thread_id: session.threadId });
    });

    try {
      const stream = this.bidChatStreamingService.streamAgentChat({
        endpointChat: this.endpointChat,
        endpointAgent: this.endpointAgent,
        threadId: session.threadId,
        agentId,
        messages: session.messages,
        conversationSummary: '',
        webAccessEnabled: false,
        modelMode: session.modelMode
      });
      await this.bidChatStreamingService.consume(stream, {
        appendToLastAssistant: (content, sources) => runInAction(() => {
          const message = session.messages[session.messages.length - 1];
          message.content += content;
          if (sources) message.sources = sources;
        }),
        replaceLastAssistant: (content, sources) => runInAction(() => {
          const message = session.messages[session.messages.length - 1];
          message.content = content;
          if (sources) message.sources = sources;
        }),
        setStatus: (status) => runInAction(() => { session.status = status; })
      });
    } catch (error) {
      runInAction(() => {
        const message = session.messages[session.messages.length - 1];
        if (message?.role === 'assistant' && !message.content.trim()) {
          message.content = error instanceof DOMException && error.name === 'AbortError'
            ? 'Antwort abgebrochen.'
            : 'Die Antwort konnte nicht erstellt werden.';
        }
      });
    } finally {
      runInAction(() => { session.status = ''; });
    }
  }

  async enhanceBidPrompt(prompt: string) {
    const agentId = this.bid_agent?.agent_azure_id;
    if (!agentId) return prompt;
    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent}/bid/${encodeURIComponent(agentId)}/enhance-prompt`,
      method: 'POST',
      body: JSON.stringify({ prompt, model_mode: this.activeBidChatSession.modelMode })
    });
    return response && typeof response === 'object' && typeof response['prompt'] === 'string'
      ? response['prompt']
      : prompt;
  }

  async getBidAgents() {
    await this.fetchBidAgents();
  }

  async getBidAgent(name: string) {
    await this.fetchBidAgent(name);
  }

  async bidReanalyzeField(value) {
    await this.reanalyze(value);
  }

  async bidReanalyzeCase() {
    await this.reanalyzeCase();
  }

  async addBidAgentDocuments(files: File[]) {
    await this.addDocuments(files);
  }

  async removeBidAgentDocument(fileId: string) {
    await this.removeDocument(fileId);
  }

  async createNewBidCase(name: string, selectedFiles: File[]) {
    return this.createNewCase(name, selectedFiles);
  }

  clearBidCaseStartError() {
    this.bidCaseStartError = '';
  }

  async closeActiveBidCase() {
    await this.closeActiveCase();
  }

  async getBidAgentFiles() {
    await this.fetchFiles();
  }

  async getFile(id: string) {
    await this.fetchFile(id);
  }

  async fetchBidAgents() {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent + '/bid'}`,
      method: 'GET'
    });

    if (!Array.isArray(response) || response.length === 0) {
      runInAction(() => {
        this.bid_agents = [];
      });
      return;
    }

    runInAction(() => {
      this.bid_agents = response;
    });
  }

  async fetchBidAgent(name) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent}/bid/${encodeURIComponent(name)}`,
      method: 'GET'
    });

    if (!response || typeof response !== 'object') {
      if (
        this.bid_agent?.agent_name === name &&
        this.bid_agent.bid_status === 'running'
      ) {
        return;
      }

      runInAction(() => {
        this.bid_agent = undefined;
      });
      return;
    }

    runInAction(() => {
      this.bid_agent = response;
    });
  }

  async reanalyze(value) {
    if (!this.bid_agent?.agent_name) return;

    runInAction(() => {
      this.bid_agent = {
        ...this.bid_agent,
        bid_status: 'running',
        bid_progress: 5,
        bid_progress_message: 'Reanalyse wurde gestartet.'
      };
    });

    await this.api.fetch({
      endpoint: `${this.endpointAgent}/bid/${encodeURIComponent(
        this.bid_agent.agent_name
      )}/reanalyze/${value}`,
      method: 'POST'
    });
  }

  async reanalyzeCase() {
    if (!this.bid_agent?.agent_name) return;

    const agentName = this.bid_agent.agent_name;

    runInAction(() => {
      this.bid_agent = {
        ...this.bid_agent,
        bid_status: 'running',
        bid_progress: 5,
        bid_progress_message: 'Gesamter Bid Case wird neu berechnet.'
      };
    });

    try {
      await this.api.fetch({
        endpoint: `${this.endpointAgent}/bid/${encodeURIComponent(
          agentName
        )}/reanalyze`,
        method: 'POST'
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Beim Starten der Neuberechnung ist ein Fehler aufgetreten.';

      runInAction(() => {
        this.bid_agent = {
          ...this.bid_agent,
          bid_status: 'failed',
          bid_progress: 100,
          bid_progress_message:
            'Bid Case konnte nicht neu berechnet werden.',
          bid_error: errorMessage
        };
      });
      throw error;
    }
  }

  async addDocuments(selectedFiles: File[]) {
    if (!this.bid_agent?.agent_name || selectedFiles.length === 0) return;

    runInAction(() => {
      this.bidRagUploadActive = true;
      this.bidRagUploadTotal = selectedFiles.length;
      this.bidRagUploadCompleted = 0;
      this.bidRagUploadCurrentFile = '';
      this.bidRagUploadFailures = [];
      this.bid_agent = {
        ...this.bid_agent,
        bid_status: 'running',
        bid_progress: 5,
        bid_progress_message: 'Dokumentenbasis wird aktualisiert.'
      };
    });

    const agentName = this.bid_agent.agent_name;

    try {
      for (const [index, file] of selectedFiles.entries()) {
        const formData = new FormData();
        formData.append('files', file);
        const shouldReanalyze = index === selectedFiles.length - 1;

        runInAction(() => {
          this.bidRagUploadCurrentFile = file.name;
        });

        try {
          await this.api.fetch({
            endpoint: `${this.endpointAgent}/bid/${encodeURIComponent(
              agentName
            )}/documents`,
            method: 'POST',
            body: formData,
            contentType: 'auto',
            queryParams: { reanalyze: shouldReanalyze ? 'true' : 'false' }
          });
        } catch {
          runInAction(() => {
            this.bidRagUploadFailures = [
              ...this.bidRagUploadFailures,
              file.name
            ];
          });
        } finally {
          runInAction(() => {
            this.bidRagUploadCompleted += 1;
          });
        }
      }

      await this.fetchBidAgent(agentName);
      await this.fetchFiles();
    } finally {
      runInAction(() => {
        this.bidRagUploadActive = false;
        this.bidRagUploadCurrentFile = '';
      });
    }
  }

  async removeDocument(fileId: string) {
    if (!this.bid_agent?.agent_name || !fileId) return;

    const agentName = this.bid_agent.agent_name;

    runInAction(() => {
      this.bid_agent = {
        ...this.bid_agent,
        bid_status: 'running',
        bid_progress: 5,
        bid_progress_message: 'Dokument wird aus der Wissensbasis entfernt.'
      };
    });

    await this.api.fetch({
      endpoint: `${this.endpointAgent}/bid/${encodeURIComponent(
        agentName
      )}/documents/${fileId}`,
      method: 'DELETE'
    });

    runInAction(() => {
      this.bid_agent_files = Array.isArray(this.bid_agent_files)
        ? this.bid_agent_files.filter((file) => file?.[1] !== fileId)
        : [];
      this.file_content = undefined;
    });

    await this.fetchBidAgent(agentName);
    await this.fetchFiles();
  }

  async createNewCase(name: string, selectedFiles: File[]): Promise<boolean> {
    const normalizedName = name.trim();

    if (!normalizedName || selectedFiles.length === 0) {
      return false;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('files', file);
    });

    runInAction(() => {
      this.bidCaseStartActive = true;
      this.bidCaseStartError = '';
    });

    try {
      const rawResponse = await this.api.fetch<unknown>({
        endpoint: `${this.endpointAgent}/bid/new/${encodeURIComponent(
          normalizedName
        )}`,
        method: 'POST',
        body: formData,
        contentType: 'auto'
      });
      const response = rawResponse instanceof Response
        ? await rawResponse.json()
        : rawResponse;

      if (
        !response ||
        typeof response !== 'object' ||
        response['status'] !== 'started' ||
        typeof response['agent_name'] !== 'string'
      ) {
        throw new Error('Der Bid Case wurde nicht vom Server bestätigt.');
      }

      runInAction(() => {
        this.bidRagUploadTotal = selectedFiles.length;
        this.bidRagUploadCompleted = selectedFiles.length;
        this.bidRagUploadCurrentFile = '';
        this.bidRagUploadFailures = [];
        this.bid_agent = {
          agent_name: response['agent_name'],
          agent_azure_id: '',
          desc: '',
          meta_information: '',
          analytics: '',
          lose: '',
          short: '',
          bid_status: response['bid_status'] || 'running',
          bid_progress: response['bid_progress'] ?? 5,
          bid_progress_message:
            response['bid_progress_message'] ||
            'Bid Case wurde gestartet. Die Dateien werden im Hintergrund verarbeitet.'
        };
        if (!this.bid_agents.includes(response['agent_name'])) {
          this.bid_agents = [...this.bid_agents, response['agent_name']];
        }
        this.bid_agent_files = [];
        this.bidCaseStartActive = false;
      });
      return true;
    } catch (error) {
      const failedCase =
        error &&
        typeof error === 'object' &&
        'data' in error &&
        error.data &&
        typeof error.data === 'object' &&
        error.data['bid_status'] === 'failed' &&
        typeof error.data['agent_name'] === 'string'
          ? error.data
          : undefined;

      if (failedCase) {
        runInAction(() => {
          this.bidCaseStartActive = false;
          this.bidCaseStartError = '';
          this.bid_agent = {
            agent_name: failedCase['agent_name'],
            agent_azure_id: '',
            desc: '',
            meta_information: '',
            analytics: '',
            lose: '',
            short: '',
            bid_status: 'failed',
            bid_progress: failedCase['bid_progress'] ?? 100,
            bid_progress_message:
              failedCase['bid_progress_message'] ||
              'Bid Case konnte nicht verarbeitet werden.',
            bid_error: failedCase['bid_error'] || ''
          };
          if (!this.bid_agents.includes(failedCase['agent_name'])) {
            this.bid_agents = [...this.bid_agents, failedCase['agent_name']];
          }
          this.bid_agent_files = [];
        });
        return true;
      }

      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String(error.message)
          : 'Beim Erstellen des Bid Cases ist ein Fehler aufgetreten.';

      runInAction(() => {
        this.bidCaseStartActive = false;
        this.bidCaseStartError = errorMessage;
        this.bidRagUploadFailures = selectedFiles.map((file) => file.name);
      });
      return false;
    }
  }

  async closeActiveCase() {
    const activeCase = this.bid_agent;
    const caseIdentifier = activeCase?.agent_azure_id || activeCase?.agent_name;

    if (!caseIdentifier) return;

    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent}/bid/delete/${encodeURIComponent(
        caseIdentifier
      )}`,
      method: 'DELETE'
    });

    if (response == null) {
      return;
    }

    runInAction(() => {
      this.bid_agent = undefined;
      this.bid_agent_files = [];
      this.file_content = undefined;
      this.bid_agents = Array.isArray(response)
        ? response
        : this.bid_agents.filter(
            (agentName) => agentName !== activeCase.agent_name
          );
    });
  }

  async fetchFiles() {
    if (this.bid_agent == undefined || !this.bid_agent.agent_azure_id) return;

    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent}/files/list/${encodeURIComponent(
        this.bid_agent.agent_azure_id
      )}`,
      method: 'GET'
    });

    if (!response || typeof response !== 'object') {
      runInAction(() => {
        this.bid_agent_files = [];
      });
      return;
    }

    runInAction(() => {
      this.bid_agent_files = response;
    });
  }

  async fetchFile(id) {
    const response = await this.api.fetch({
      endpoint: `${this.endpointAgent}/files/${encodeURIComponent(id)}`,
      method: 'GET'
    });

    if (!response || typeof response !== 'object') {
      runInAction(() => {
        this.file_content = undefined;
      });
      return;
    }

    const blob = await response.blob();

    runInAction(() => {
      this.file_content = blob;
    });
  }
}
